import { Box, Divider, Tab, Tabs, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useState } from 'react';
import theme from '../../theme';
import UpcomingReservation from './UpcomingReservation';
import HistoryReservation from './HistoryReservation';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: theme.palette.custom.sidebarActiveBg,
      borderRadius: '6px',
    },
  },
});

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  return value === index ? <>{children}</> : null;
};

interface OrderManagementTabsProps {
  selectedStore: any;
  isStoresLoading: boolean;
}

const ReservationManagementTabs: React.FC<OrderManagementTabsProps> = ({
  selectedStore,
  isStoresLoading,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date();
    const lastMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate()
    );
    return lastMonth;
  });
  const [toDate, setToDate] = useState<Date | null>(() => new Date());

  const tabItems = [
    {
      label: 'Upcoming',
      component: (
        <UpcomingReservation
          selectedStore={selectedStore}
          isStoresLoading={isStoresLoading}
        />
      ),
    },
    {
      label: 'History',
      component: (
        <HistoryReservation
          fromDate={fromDate || new Date()}
          toDate={toDate || new Date()}
          selectedStore={selectedStore}
          isStoresLoading={isStoresLoading}
        />
      ),
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const commonBoxStyles = {
    display: 'flex',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFF',
    height: '100%',
  };

  const commonContentStyles = {
    width: '100%',
  };

  return (
    <Box sx={commonBoxStyles}>
      <Box sx={commonContentStyles}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            padding: '10px 16px',
          }}
        >
          <StyledTabs value={selectedTab} onChange={handleTabChange}>
            {tabItems.map((tab, i) => (
              <Tab key={i} label={tab.label} />
            ))}
          </StyledTabs>

          {selectedTab === 1 && (
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DemoContainer components={['DatePicker']}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    views={['year', 'month', 'day']}
                    onChange={(newValue: Date | null) => setFromDate(newValue)}
                    format="dd-MM-yyyy"
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& input': {
                            fontSize: '14px',
                            color: theme.palette.lightGray.text,
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="To Date"
                    views={['year', 'month', 'day']}
                    value={toDate}
                    onChange={(newValue: Date | null) => setToDate(newValue)}
                    format="dd-MM-yyyy"
                    minDate={fromDate || undefined}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& input': {
                            fontSize: '14px',
                            color: theme.palette.lightGray.text,
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            height: 'calc(100vh - 200px)',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            p: 2,
          }}
        >
          {tabItems.map((tab, index) => (
            <TabPanel key={index} value={selectedTab} index={index}>
              {tab?.component || null}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ReservationManagementTabs;
